import apiClient from './apiClient';

const isDevelopment = process.env.NODE_ENV === 'development';
const debugLog = (...args) => {
  if (isDevelopment) {
    console.log(...args);
  }
};
const debugWarn = (...args) => {
  if (isDevelopment) {
    console.warn(...args);
  }
};

class CampaignService {
  async getAllCampaigns() {
    try {
      debugLog('Fetching all campaigns');
      const response = await apiClient.get('/campaigns');
      debugLog('Campaigns API response:', response);
      return response;
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }
  }

  async getCampaignsByBrand(brandId) {
    try {
      debugLog(`Fetching campaigns for brand ID: ${brandId}`);
      const response = await apiClient.get(`/campaigns?brandId=${brandId}`);
      debugLog('Campaigns by brand API response:', response);
      return response;
    } catch (error) {
      console.error(`Error fetching campaigns for brand ${brandId}:`, error);
      throw error;
    }
  }

  async getCampaignById(id) {
    try {
      const response = await apiClient.get(`/campaigns/${id}`);
      return response;
    } catch (error) {
      console.error('Error fetching campaign:', error);
      throw error;
    }
  }

  async createCampaign(data) {
    try {
      debugLog('Creating campaign with data:', data);
      const response = await apiClient.post('/campaigns', data);
      debugLog('Create campaign response:', response);
      return response;
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  }

  async updateCampaign(id, data) {
    try {
      debugLog('Updating campaign with ID:', id, 'data:', data);
      const response = await apiClient.patch(`/campaigns/${id}`, data);
      debugLog('Update campaign response:', response);
      return response;
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  }

  async finishCampaign(id) {
    try {
      debugLog(`Finishing campaign with ID: ${id}`);
      const response = await apiClient.put(`/campaigns/${id}/finish`);
      debugLog('Finish campaign response:', response);
      return response;
    } catch (error) {
      console.error('Error finishing campaign:', error);
      throw error;
    }
  }

  async updateCampaignStatus(id, status) {
    try {
      const response = await apiClient.post(`/campaigns/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating campaign status:', error);
      throw error;
    }
  }

  async updatePricingType(id, pricingType) {
    try {
      debugLog(`Updating pricing type for campaign ${id} to ${pricingType}`);
      
      // Ensure we're sending the correct data format
      const requestData = { pricingType };
      debugLog('Request data:', requestData);
      
      const response = await apiClient.put(`/campaigns/${id}/pricing-type`, requestData);
      debugLog('Update pricing type response:', response);
      return response.data;
    } catch (error) {
      console.error('Error updating campaign pricing type:', error);
      throw error;
    }
  }

  async getCampaignCreators(campaignId) {
    try {
      debugLog(`Fetching campaign creators for campaign: ${campaignId}`);
      
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      
      // First get the basic creator data from the campaign with timestamp
      // Make sure we're using the correct URL pattern (no /api prefix as it's added by the gateway)
      const response = await apiClient.get(`/campaigns/${campaignId}/creators?t=${timestamp}`);
      debugLog('Campaign creators response:', response.data ? 'Data present' : 'No data');
      const campaignCreators = response.data.creators || [];
      
      // If we have creators, fetch their complete analytics data
      if (campaignCreators.length > 0) {
        // Extract creator IDs
        const creatorIds = campaignCreators
          .filter(creator => creator._id)
          .map(creator => creator._id);
        
        if (creatorIds.length > 0) {
          debugLog(`Fetching analytics data for ${creatorIds.length} creators`);
          
          try {
            // Add another timestamp for this request
            const timestamp2 = new Date().getTime();
            
            // Use the with-analytics endpoint to get complete analytics data with timestamp
            // Make sure we're using the correct URL pattern (no /api prefix as it's added by the gateway)
            debugLog(`Fetching analytics with URL: /creators/with-analytics?ids=${creatorIds.join(',')}&campaignId=${campaignId}&t=${timestamp2}`);
            const analyticsResponse = await apiClient.get(
              `/creators/with-analytics?ids=${creatorIds.join(',')}&campaignId=${campaignId}&t=${timestamp2}`
            );
            
            // Log the raw analytics data for debugging
            debugLog('Raw analytics response:', analyticsResponse.data ? 'Data present' : 'No data');
            
            // Check if the response has the new format with creators property
            const analyticsData = analyticsResponse.data?.creators || analyticsResponse.data;
            
            if (analyticsData && Array.isArray(analyticsData)) {
              debugLog(`Received analytics data for ${analyticsData.length} creators`);
              
              // Create a map of creators with analytics data
              const creatorAnalyticsMap = new Map();
              analyticsData.forEach(creator => {
                if (creator && creator._id) {
                  creatorAnalyticsMap.set(creator._id.toString(), creator);
                }
              });
              
              // Merge analytics data with campaign creators
              const creatorsWithAnalytics = campaignCreators.map(creator => {
                const creatorId = creator._id.toString();
                const creatorWithAnalytics = creatorAnalyticsMap.get(creatorId);
                
                if (creatorWithAnalytics) {
                  debugLog(`Found analytics data for creator ${creatorId}`);
                  
                  // Use the new analytics format which is already properly structured by the adapter
                  return {
                    ...creator,
                    analytics: creatorWithAnalytics.analytics || {}
                  };
                }
                
                return creator;
              });
              
              // Return creators with analytics data
              return {
                data: {
                  creators: creatorsWithAnalytics,
                  briefingMessage: response.data.briefingMessage || {},
                  deliveryChannel: response.data.deliveryChannel || {}
                }
              };
            }
          } catch (analyticsError) {
            console.error('Error fetching creator analytics:', analyticsError);
            console.error('Error details:', {
              message: analyticsError.message,
              response: analyticsError.response?.data,
              status: analyticsError.response?.status,
              url: analyticsError.config?.url
            });
            // Continue with basic creator data if analytics fetch fails
          }
        }
      }
      
      // Return basic creator data if analytics fetch fails or no creators
      return {
        data: {
          creators: campaignCreators,
          briefingMessage: response.data.briefingMessage || {},
          deliveryChannel: response.data.deliveryChannel || {}
        }
      };
    } catch (error) {
      console.error('Error fetching campaign creators:', error);
      throw error;
    }
  }

  async getRemovedCreators(campaignId) {
    try {
      const response = await apiClient.get(`/campaigns/${campaignId}/creators/removed`);
      return response.data;
    } catch (error) {
      console.error('Error fetching removed creators:', error);
      throw error;
    }
  }

  async removeCreator(campaignId, creatorId) {
    try {
      const response = await apiClient.delete(`/campaigns/${campaignId}/creators/${creatorId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing creator:', error);
      throw error;
    }
  }

  async restoreCreator(campaignId, creatorId) {
    try {
      const response = await apiClient.post(`/campaigns/${campaignId}/creators/${creatorId}/restore`);
      return response.data;
    } catch (error) {
      console.error('Error restoring creator:', error);
      throw error;
    }
  }

  async deleteCreator(campaignId, creatorId) {
    try {
      const response = await apiClient.delete(`/campaigns/${campaignId}/creators/${creatorId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting creator:', error);
      throw error;
    }
  }

  async updateCreatorStatus(campaignId, creatorId, statusData) {
    try {
      // statusData can contain { briefingStatus } or { deliveryStatus } or both
      const response = await apiClient.post(`/campaigns/${campaignId}/creators/${creatorId}/status`, statusData);
      return response.data;
    } catch (error) {
      console.error('Error updating creator status:', error);
      throw error;
    }
  }

  async updateCreatorBriefingStatus(campaignId, creatorId, briefingStatus) {
    try {
      // Use the original endpoint that handles Discord setup
      const response = await apiClient.post(`/campaigns/${campaignId}/creators/${creatorId}/status`, { briefingStatus });
      return response.data;
    } catch (error) {
      console.error('Error updating creator briefing status:', error);
      throw error;
    }
  }

  async updateCreatorDeliveryStatus(campaignId, creatorId, deliveryStatus) {
    try {
      // Use the correct route path: /campaign-creators instead of /campaigns/.../creators
      const response = await apiClient.post(`/campaign-creators/${campaignId}/creators/${creatorId}/delivery-status`, { deliveryStatus });
      return response.data;
    } catch (error) {
      console.error('Error updating creator delivery status:', error);
      throw error;
    }
  }

  async archiveCampaign(id) {
    try {
      const response = await apiClient.delete(`/campaigns/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error archiving campaign:', error);
      throw error;
    }
  }
  
  /**
   * Get complete report data for a campaign using the centralized creator service
   * This endpoint combines campaign data, analytics, and normalized creator data
   * @param {string} campaignId - The campaign ID
   * @param {string} cacheParam - Optional cache-busting parameter
   * @returns {Promise<Object>} - Combined campaign report data
   */
  async getReportData(campaignId, cacheParam = '') {
    try {
      const cacheBuster = cacheParam || Date.now();
      const isStaging = window.location.hostname.includes('staging');
      
      // Add detailed logging for debugging
      debugLog(`Fetching report data for campaign ${campaignId} in ${isStaging ? 'staging' : 'local'} environment`);
      
      // Try the original endpoint first that was working before
      debugLog(`Trying original report-data endpoint for ${campaignId}...`);
      try {
        const reportResponse = await apiClient.get(`/campaigns/${campaignId}/report-data?t=${cacheBuster}`);
        
        if (reportResponse.data && (reportResponse.data.creators || []).length > 0) {
          debugLog(`Success! Original endpoint returned ${reportResponse.data.creators?.length || 0} creators`);
          return reportResponse;
        } else {
          debugLog('Original endpoint returned no creators, trying fallback approach...');
        }
      } catch (reportError) {
        debugWarn('Original endpoint failed, trying fallback approach...', {
          status: reportError.response?.status,
          message: reportError.message
        });
      }
      
      // Fallback approach: fetch campaign and creators separately
      debugLog(`Fetching campaign details for ${campaignId}...`);
      const campaignResponse = await apiClient.get(`/campaigns/${campaignId}`);
      
      if (!campaignResponse.data) {
        throw new Error(`Campaign ${campaignId} not found`);
      }
      
      // Log campaign details for debugging
      debugLog('Campaign details:', {
        id: campaignResponse.data._id,
        name: campaignResponse.data.name,
        status: campaignResponse.data.status
      });
      
      // Then fetch the creators
      debugLog(`Fetching creators for campaign ${campaignId}...`);
      const creatorsResponse = await apiClient.get(`/campaigns/${campaignId}/creators?t=${cacheBuster}`);
      
      // Log creators response
      debugLog('Creators response:', {
        status: creatorsResponse.status,
        hasData: !!creatorsResponse.data,
        creatorCount: Array.isArray(creatorsResponse.data) ? creatorsResponse.data.length : 'not an array'
      });
      
      // Try to fetch analytics separately
      debugLog(`Fetching analytics for campaign ${campaignId}...`);
      let analyticsData = {};
      try {
        const analyticsResponse = await apiClient.get(`/campaigns/${campaignId}/analytics?t=${cacheBuster}`);
        analyticsData = analyticsResponse.data || {};
        debugLog('Analytics data:', {
          hasData: !!analyticsData,
          keys: Object.keys(analyticsData)
        });
      } catch (analyticsError) {
        debugWarn('Failed to fetch analytics, continuing with empty analytics:', analyticsError.message);
      }
      
      // Combine the data into the expected format
      const combinedData = {
        campaign: campaignResponse.data,
        creators: Array.isArray(creatorsResponse.data) ? creatorsResponse.data : [],
        analytics: analyticsData
      };
      
      // Log final combined data
      debugLog(`Final report data assembled for campaign ${campaignId}:`, {
        hasData: true,
        hasCreators: combinedData.creators.length,
        creatorIds: combinedData.creators.slice(0, 3).map(c => c._id || 'unknown')
      });
      
      // Return in the format expected by the component
      return { data: combinedData };
    } catch (error) {
      // Enhanced error logging
      console.error('Error fetching report data:', {
        message: error.message,
        status: error.response?.status,
        url: error.config?.url
      });
      throw error;
    }
  }

  /**
   * Trigger Twitter data sync for a campaign
   * @param {string} campaignId - The campaign ID
   * @returns {Promise} - The API response
   */
  async syncCampaignTwitterData(campaignId) {
    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      
      // Make API request to trigger the sync
      const response = await apiClient.post(
        `/campaigns/${campaignId}/sync-twitter?t=${timestamp}`
      );
      
      return response;
    } catch (error) {
      console.error('Error syncing Twitter data:', error);
      throw error;
    }
  }

  async getReactions(campaignId) {
    try {
      const response = await apiClient.get(`/campaigns/${campaignId}/reactions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign reactions:', error);
      throw error;
    }
  }

  async syncReactions(campaignId) {
    try {
      const response = await apiClient.post(`/campaigns/${campaignId}/reactions/sync`);
      return response.data;
    } catch (error) {
      console.error('Error syncing campaign reactions:', error);
      throw error;
    }
  }

  // --- Group Management ---

  async createGroup(campaignId, { name, maxSize } = {}) {
    try {
      const response = await apiClient.post(`/campaigns/${campaignId}/groups`, { name, maxSize });
      return response.data;
    } catch (error) {
      console.error('Error creating group:', error);
      throw error;
    }
  }

  async getGroups(campaignId) {
    try {
      const response = await apiClient.get(`/campaigns/${campaignId}/groups`);
      return response.data;
    } catch (error) {
      console.error('Error fetching groups:', error);
      throw error;
    }
  }

  async updateGroup(campaignId, groupId, data) {
    try {
      const response = await apiClient.put(`/campaigns/${campaignId}/groups/${groupId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  }

  async deleteGroup(campaignId, groupId) {
    try {
      const response = await apiClient.delete(`/campaigns/${campaignId}/groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting group:', error);
      throw error;
    }
  }

  async activateGroup(campaignId, groupId, { briefUrl, message } = {}) {
    try {
      const body = {};
      if (briefUrl) body.briefUrl = briefUrl;
      if (message) body.message = message;
      const response = await apiClient.post(`/campaigns/${campaignId}/groups/${groupId}/activate`, body);
      return response.data;
    } catch (error) {
      console.error('Error activating group:', error);
      throw error;
    }
  }

  async sendGoLiveMessage(campaignId, groupId, { briefUrl, message, channelMode } = {}) {
    try {
      const body = {};
      if (briefUrl) body.briefUrl = briefUrl;
      if (message) body.message = message;
      if (channelMode) body.channelMode = channelMode;
      const response = await apiClient.post(`/campaigns/${campaignId}/groups/${groupId}/go-live`, body);
      return response.data;
    } catch (error) {
      console.error('Error sending go-live message:', error);
      throw error;
    }
  }

  async deactivateGroup(campaignId, groupId) {
    try {
      const response = await apiClient.post(`/campaigns/${campaignId}/groups/${groupId}/deactivate`);
      return response.data;
    } catch (error) {
      console.error('Error deactivating group:', error);
      throw error;
    }
  }

  async assignCreatorsToGroup(campaignId, groupId, creatorIds) {
    try {
      const response = await apiClient.post(
        `/campaigns/${campaignId}/groups/${groupId}/creators`,
        { creatorIds }
      );
      return response.data;
    } catch (error) {
      console.error('Error assigning creators to group:', error);
      throw error;
    }
  }

  async removeCreatorFromGroup(campaignId, groupId, creatorId) {
    try {
      const response = await apiClient.delete(
        `/campaigns/${campaignId}/groups/${groupId}/creators/${creatorId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error removing creator from group:', error);
      throw error;
    }
  }

  async getGroupContent(campaignId, groupId) {
    try {
      const response = await apiClient.get(
        `/campaigns/${campaignId}/groups/${groupId}/content`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching group content:', error);
      throw error;
    }
  }

  async submitCreatorPost(campaignId, creatorId, tweetUrl, groupId = null) {
    try {
      const body = { tweetUrl };
      if (groupId) body.groupId = groupId;
      const response = await apiClient.post(
        `/campaigns/${campaignId}/creators/${creatorId}/post`,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Error submitting creator post:', error);
      throw error;
    }
  }

  async duplicateGroup(campaignId, groupId) {
    try {
      const response = await apiClient.post(
        `/campaigns/${campaignId}/groups/${groupId}/duplicate`
      );
      return response.data;
    } catch (error) {
      console.error('Error duplicating group:', error);
      throw error;
    }
  }

  async addCreatorToCampaign(campaignId, { twitterUsername }) {
    const response = await apiClient.post(`/campaigns/${campaignId}/creators`, { twitterUsername });
    // apiClient's validateStatus accepts 4xx — throw manually so callers can catch
    if (response.status >= 400) {
      const error = new Error(response.data?.message || 'Failed to add creator');
      error.response = response;
      throw error;
    }
    return response.data;
  }

  async setupAmplificationTracking(campaignId, trackingData) {
    try {
      debugLog(`Setting up amplification tracking for campaign ${campaignId}`, trackingData);
      
      const response = await apiClient.post(`/campaigns/${campaignId}/amplification/tracking`, trackingData);
      
      debugLog('Amplification tracking response:', response);
      return response.data;
    } catch (error) {
      console.error('Error setting up amplification tracking:', error);
      throw error;
    }
  }

  async updateManualImpressions(campaignId, creatorId, impressions) {
    const response = await apiClient.post(
      `/campaign-creators/${campaignId}/creators/${creatorId}/manual-impressions`,
      { impressions }
    );
    return response.data;
  }
}

// Export as both default and named export to maintain compatibility
const campaignService = new CampaignService();
export { campaignService };
export default campaignService;
